"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Bot, MessageSquare, Calendar, Star, ChartBar as BarChart3, Play, Pause, ChevronLeft, ChevronRight, CircleCheck as CheckCircle2, Clock, ArrowRight, Sparkles, ShieldCheck, Send, Cpu, Layers, Activity } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const STAGES = [{
  id: 1,
  stepNum: "01",
  label: "Lead Capture",
  title: "Lead Capture & Instant AI Audit",
  trigger: "Form Submission or Missed Call",
  description: "Captures prospect inquiries instantly across web forms, calls, or chat widgets, triggering an immediate background AI visibility analysis across LLMs.",
  channelList: "Web Form, SMS, AI Engine",
  inboundLabel: "Inbound Lead",
  inboundMsg: "New lead request: 'Need emergency roofing check in Austin'",
  inboundTime: "10:42:01 AM",
  outboundLabel: "System Dispatch",
  outboundMsg: "AI Audit Initiated: Querying ChatGPT & Gemini citations for local business presence...",
  outboundTime: "10:42:03 AM",
  badgeText: "Response < 1.5s",
  highlightOne: "Zero-latency lead capture",
  highlightTwo: "Automatic lead deduplication",
  highlightThree: "Multi-platform citation lookup"
}, {
  id: 2,
  stepNum: "02",
  label: "AI Qualification",
  title: "Instant Lead Qualification & Outreach",
  trigger: "New Unqualified Lead Detected",
  description: "Intelligent AI assistant evaluates service requirements, urgency, and project scope before starting personalized text and email dialogues.",
  channelList: "SMS, Email, CRM Webhook",
  inboundLabel: "Prospect Reply",
  inboundMsg: "Yes, I need someone to inspect a roof leak today if possible.",
  inboundTime: "10:42:45 AM",
  outboundLabel: "AI Assistant",
  outboundMsg: "We have an emergency slot open today at 2:00 PM. Would you like to reserve it?",
  outboundTime: "10:42:48 AM",
  badgeText: "24/7 Availability",
  highlightOne: "Intelligent appointment matching",
  highlightTwo: "Custom response parameters",
  highlightThree: "Instant CRM lead scoring"
}, {
  id: 3,
  stepNum: "03",
  label: "Auto Booking",
  title: "Automated Booking & Calendar Sync",
  trigger: "Time Slot Selected by Client",
  description: "Schedules appointments directly into GoHighLevel or Google Calendar, dispatches SMS calendar invites, and queues multi-stage automated reminders.",
  channelList: "GHL Calendar, SMS, Email",
  inboundLabel: "Booking Confirmed",
  inboundMsg: "Client reserved: Today at 2:00 PM (Roof Inspection)",
  inboundTime: "10:43:12 AM",
  outboundLabel: "Calendar Automation",
  outboundMsg: "Appointment Confirmed! SMS confirmation and Google Calendar link delivered.",
  outboundTime: "10:43:14 AM",
  badgeText: "Zero No-Shows",
  highlightOne: "Two-way SMS confirmation",
  highlightTwo: "Automated 2hr & 15m reminders",
  highlightThree: "Real-time calendar synchronization"
}, {
  id: 4,
  stepNum: "04",
  label: "Review Engine",
  title: "Post-Service Review Generation",
  trigger: "Job Status Updated to 'Completed'",
  description: "Automatically dispatches friendly review requests via SMS and Email following job completion to boost local AI search ratings and citations.",
  channelList: "SMS, Google Reviews, CRM",
  inboundLabel: "Job Completed",
  inboundMsg: "Service record updated: Inspection completed for Austin Roofing lead.",
  inboundTime: "03:15:00 PM",
  outboundLabel: "Reputation Engine",
  outboundMsg: "Hi Alex! How was your service today? Tap here to leave a quick 5-star review!",
  outboundTime: "03:30:00 PM",
  badgeText: "+85% Review Rate",
  highlightOne: "Smart review link routing",
  highlightTwo: "Private feedback channel for low ratings",
  highlightThree: "Real-time score tracking"
}, {
  id: 5,
  stepNum: "05",
  label: "Smart Nurture",
  title: "Multi-Channel Follow-Up & Nurture",
  trigger: "Unbooked Lead > 48 Hours",
  description: "Re-engages hesitant prospects with tailored educational SMS and email sequences highlighting AI visibility reports and customer case studies.",
  channelList: "SMS, Email, WhatsApp",
  inboundLabel: "Dormant Trigger",
  inboundMsg: "Lead inactive for 48 hours without scheduled appointment.",
  inboundTime: "Day 2 - 10:00 AM",
  outboundLabel: "Nurture Campaign",
  outboundMsg: "Hi Alex, still looking for roof repair? Here is our 5-minute guide on storm damage prep.",
  outboundTime: "Day 2 - 10:01 AM",
  badgeText: "3x Recycled Leads",
  highlightOne: "Automated behavior-based triggers",
  highlightTwo: "Value-first content delivery",
  highlightThree: "Built-in unsubscribe compliance"
}, {
  id: 6,
  stepNum: "06",
  label: "ROI Analytics",
  title: "Monthly ROI & AI Citation Reporting",
  trigger: "1st Day of Month / On-Demand",
  description: "Aggregates conversion data, citation growth on LLMs, call volumes, and revenue metrics into an executive PDF report and automated client dispatch.",
  channelList: "PDF Report, Email, Dashboard",
  inboundLabel: "Monthly Cycle",
  inboundMsg: "End-of-month reporting cycle executed.",
  inboundTime: "1st - 08:00 AM",
  outboundLabel: "Reporting Engine",
  outboundMsg: "Monthly Report Ready: +42 AI Citations, 18 New Bookings, $34,200 estimated pipeline.",
  outboundTime: "1st - 08:02 AM",
  badgeText: "Full Transparency",
  highlightOne: "Real-time ROI dashboard",
  highlightTwo: "LLM citation rank tracking",
  highlightThree: "Automated executive summary"
}];
const LEGEND_ITEMS = [{
  id: 1,
  name: "Lead Triggers",
  detail: "Web & Phone Ingestion"
}, {
  id: 2,
  name: "AI Evaluation",
  detail: "LLM Citation Audit"
}, {
  id: 3,
  name: "Messaging",
  detail: "SMS & Email Engine"
}, {
  id: 4,
  name: "Scheduling",
  detail: "GHL Calendar Sync"
}, {
  id: 5,
  name: "Reputation",
  detail: "Review Generation"
}, {
  id: 6,
  name: "Analytics",
  detail: "Monthly Dashboard"
}];
export default function Workflow() {
  const [activeStage, setActiveStage] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!isPlaying) return;
    const intervalTime = 50;
    const totalTime = 6000;
    const increment = intervalTime / totalTime * 100;
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          setActiveStage(current => (current + 1) % STAGES.length);
          return 0;
        }
        return prev + increment;
      });
    }, intervalTime);
    return () => clearInterval(timer);
  }, [isPlaying]);
  const handleStageSelect = (index: number) => {
    setActiveStage(index);
    setProgress(0);
  };
  const handlePrev = () => {
    setActiveStage(prev => prev === 0 ? STAGES.length - 1 : prev - 1);
    setProgress(0);
  };
  const handleNext = () => {
    setActiveStage(prev => (prev + 1) % STAGES.length);
    setProgress(0);
  };
  const currentStageData = STAGES[activeStage];
  return <section id="howitworksautomationworkflow" data-nav="light" className="relative w-full bg-background text-foreground py-16 md:py-24 border-b border-border overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary">
            <Zap className="size-3.5 mr-1 text-destructive" />
            AUTOMATION ENGINE WORKFLOW
          </Badge>

          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight leading-tight text-foreground mb-4">
            How ClearLine Turns AI Inquiries Into{" "}
            <span className="font-serif italic !text-[var(--accent-deep-red)]">Booked Revenue</span>
          </h2>

          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Our 6-stage automation pipeline connects every step of your customer journey from initial AI recommendation to verified 5-star reviews.
          </p>
        </div>

        {}
        <div className="mb-8 bg-card border border-border rounded-xl p-4 shadow-sm">
          {}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4 pb-3 border-b border-border">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="font-mono text-xs">
                STAGE {currentStageData.stepNum} / 06
              </Badge>
              <span className="text-sm font-medium text-foreground">
                {currentStageData.title}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setIsPlaying(!isPlaying)} className="h-8 gap-1.5 text-xs" aria-label={isPlaying ? "Pause auto advance" : "Play auto advance"}>
                {isPlaying ? <>
                    <Pause className="size-3.5" /> Pause Auto
                  </> : <>
                    <Play className="size-3.5" /> Auto Play
                  </>}
              </Button>

              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" onClick={handlePrev} className="size-8" aria-label="Previous Stage">
                  <ChevronLeft className="size-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={handleNext} className="size-8" aria-label="Next Stage">
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            </div>
          </div>

          {}
          <Progress value={progress} className="h-1 mb-4 bg-muted" />

          {}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            {STAGES.map((stage, i) => {
            const isActive = i === activeStage;
            return <Button key={stage.id} data-index={i} variant={isActive ? "default" : "outline"} onClick={() => handleStageSelect(i)} className="gap-2 whitespace-nowrap rounded-md text-sm font-medium [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive has-[>svg]:px-3 h-auto py-2.5 px-3 flex flex-col items-start text-left justify-center transition-all text-primary-foreground shadow-sm ring-1 ring-white border-white !bg-[var(--accent-deep-red)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90">
                  <div className="flex items-center justify-between w-full mb-1">
                    <span className="text-[10px] font-mono opacity-80">
                      {stage.stepNum}
                    </span>
                    {isActive && <span className="size-1.5 rounded-full bg-destructive animate-pulse" />}
                  </div>
                  <span className="text-xs font-semibold truncate w-full">
                    {stage.label}
                  </span>
                </Button>;
          })}
          </div>
        </div>

        {}
        <AnimatePresence mode="wait">
          <motion.div key={activeStage} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -12
        }} transition={{
          duration: 0.35
        }}>
            <Card className="border border-border bg-card shadow-md">
              <CardHeader className="border-b border-border pb-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="default" className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-primary/90 text-white border-transparent !bg-[var(--accent-deep-red)] dark:aria-invalid:ring-destructive/40">
                        Triggered Action
                      </Badge>
                      <span className="text-xs text-muted-foreground font-mono">
                        {currentStageData.trigger}
                      </span>
                    </div>
                    <CardTitle className="text-2xl md:text-3xl font-semibold text-card-foreground">
                      {currentStageData.title}
                    </CardTitle>
                  </div>

                  <Badge variant="outline" className="w-fit text-xs font-mono py-1 px-3 border-border bg-background">
                    <Sparkles className="size-3.5 mr-1 text-destructive" />
                    {currentStageData.badgeText}
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="pt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                {}
                <div className="lg:col-span-7 flex flex-col gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-2">
                      Stage Description
                    </h3>
                    <CardDescription className="text-foreground text-base leading-relaxed">
                      {currentStageData.description}
                    </CardDescription>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                      Active Automation Highlights
                    </h3>
                    <ul className="space-y-2.5">
                      <li className="flex items-center gap-2.5 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-destructive shrink-0" />
                        <span>{currentStageData.highlightOne}</span>
                      </li>
                      <li className="flex items-center gap-2.5 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-destructive shrink-0" />
                        <span>{currentStageData.highlightTwo}</span>
                      </li>
                      <li className="flex items-center gap-2.5 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-destructive shrink-0" />
                        <span>{currentStageData.highlightThree}</span>
                      </li>
                    </ul>
                  </div>

                  <Separator className="my-1" />

                  <div>
                    <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                      Integrated Channels
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {currentStageData.channelList.split(", ").map((ch, idx) => <Badge key={idx} data-index={idx} variant="secondary" className="text-xs font-mono py-1">
                          {ch}
                        </Badge>)}
                    </div>
                  </div>
                </div>

                {}
                <div className="lg:col-span-5 bg-background border border-border rounded-xl p-4 shadow-inner flex flex-col gap-4">
                  <div className="flex items-center justify-between pb-3 border-b border-border">
                    <div className="flex items-center gap-2">
                      <Activity className="size-4 text-destructive" />
                      <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
                        Live Sequence Stream
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[11px] font-mono text-muted-foreground">
                      <span className="size-2 rounded-full bg-emerald-500 animate-pulse" />
                      ACTIVE
                    </span>
                  </div>

                  {}
                  <div className="flex flex-col gap-1 items-start max-w-[90%]">
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-mono">
                      <span>{currentStageData.inboundLabel}</span>
                      <span>bull;</span>
                      <span>{currentStageData.inboundTime}</span>
                    </div>
                    <div className="bg-muted text-muted-foreground p-3 rounded-lg text-xs leading-relaxed border border-border">
                      {currentStageData.inboundMsg}
                    </div>
                  </div>

                  {}
                  <div className="flex flex-col gap-1 items-end self-end max-w-[90%]">
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-mono">
                      <span>{currentStageData.outboundLabel}</span>
                      <span>bull;</span>
                      <span>{currentStageData.outboundTime}</span>
                    </div>
                    <div className="text-primary-foreground p-3 rounded-lg text-xs leading-relaxed shadow-sm !bg-[var(--accent-deep-red)]">
                      {currentStageData.outboundMsg}
                    </div>
                  </div>

                  <div className="pt-2 flex items-center justify-between text-[11px] font-mono text-muted-foreground border-t border-border">
                    <span className="flex items-center gap-1">
                      <Clock className="size-3" /> Auto-Dispatched
                    </span>
                    <span className="font-semibold !text-[var(--accent-deep-red)]">
                      ClearLine Engine
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>

        {}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h3 className="text-base font-semibold text-foreground">
                Automation Node Quick Reference
              </h3>
              <p className="text-xs text-muted-foreground">
                Key components driving the ClearLine workflow engine
              </p>
            </div>
            <Badge variant="outline" className="w-fit text-xs font-mono">
              6 CORE MODULES
            </Badge>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {LEGEND_ITEMS.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col p-3 rounded-lg bg-card border border-border hover:border-primary/50 transition-colors">
                <div className="flex items-center gap-2 mb-1">
                  {i === 0 && <Zap className="size-4 text-destructive" />}
                  {i === 1 && <Bot className="size-4 text-destructive" />}
                  {i === 2 && <MessageSquare className="size-4 text-destructive" />}
                  {i === 3 && <SafeHydrate><Calendar className="size-4 text-destructive" /></SafeHydrate>}
                  {i === 4 && <Star className="size-4 text-destructive" />}
                  {i === 5 && <BarChart3 className="size-4 text-destructive" />}
                  <span className="text-xs font-semibold text-foreground">
                    {item.name}
                  </span>
                </div>
                <span className="text-[11px] text-muted-foreground">
                  {item.detail}
                </span>
              </div>)}
          </div>
        </div>

        {}
        <div className="mt-12 text-primary-foreground rounded-2xl p-8 flex flex-col items-center justify-between gap-6 shadow-lg !bg-[rgba(0,0,0,1)] md:p-10 md:flex-row">
          <div className="max-w-xl">
            <h3 className="text-2xl md:text-3xl font-semibold mb-2">
              Ready to automate your local business lead flow?
            </h3>
            <p className="text-primary-foreground/80 text-sm md:text-base">
              Set up your automated visibility engine and schedule appointments on autopilot.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full md:w-auto">
            <Button size="lg" variant="secondary" asChild className="text-secondary-foreground inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive shadow-xs h-10 rounded-md px-6 has-[>svg]:px-4 w-full !text-[rgba(255,255,255,1)] !bg-[var(--accent-deep-red)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-secondary/80 sm:w-auto">
              <Link to="/PricingPlansPage"></Link>
            </Button>
            <Button size="lg" variant="outline" asChild className="w-full sm:w-auto bg-transparent text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/10 hover:text-primary-foreground">
              <Link to="/VisibilityCheckupPage">
                Free AI Checkup
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}